import { IAvailableCurrency } from 'services/BackendApi/types/AvailableCurrenciesResponse';
import { TCurrencyCode } from 'interfaces';

/**
 * Map of currency ISO codes to ISO 3166-1 alpha-2 country codes.
 * Used by react-world-flags to render flag SVGs.
 * Extend as needed when new currencies are added.
 */
export const CURRENCY_COUNTRY_CODE_MAP: Record<string, string> = {
  GBP: 'GB',
  JPY: 'JP',
  CAD: 'CA',
  AUD: 'AU',
  CHF: 'CH',
  CNY: 'CN',
  INR: 'IN',
  MXN: 'MX',
  BRL: 'BR',
  KRW: 'KR',
  SGD: 'SG',
  HKD: 'HK',
  NOK: 'NO',
  SEK: 'SE',
  DKK: 'DK',
  NZD: 'NZ',
  ZAR: 'ZA',
  RUB: 'RU',
  TRY: 'TR',
  PLN: 'PL',
  THB: 'TH',
  IDR: 'ID',
  MYR: 'MY',
  PHP: 'PH',
  CZK: 'CZ',
  HUF: 'HU',
  ILS: 'IL',
  CLP: 'CL',
  AED: 'AE',
  SAR: 'SA',
  QAR: 'QA',
  KWD: 'KW',
  BHD: 'BH',
  OMR: 'OM',
  TWD: 'TW',
  ARS: 'AR',
  COP: 'CO',
  PEN: 'PE',
  EGP: 'EG',
  MVR: 'MV',
  SCR: 'SC',
  MUR: 'MU',
  LKR: 'LK',
  USD: 'US',
  EUR: 'EU',
};

/**
 * Get the country code for a currency code, for use with react-world-flags.
 * Returns undefined if no mapping exists.
 */
export const getCountryCodeForCurrency = (currencyCode: string): string | undefined => {
  return CURRENCY_COUNTRY_CODE_MAP[currencyCode];
};

/**
 * Convert a price from a hotel's native currency to the selected display currency.
 *
 * @param amount - The price in the hotel's native currency
 * @param hotelCurrency - The hotel's native currency code (e.g. 'USD', 'EUR')
 * @param selectedCurrencyData - The selected currency data from the available-currencies endpoint
 * @returns The converted price, or the original amount if no matching rate is found
 */
export const convertPrice = (
  amount: number,
  hotelCurrency: TCurrencyCode,
  selectedCurrencyData: IAvailableCurrency
): number => {
  const rate = selectedCurrencyData.rates.find(r => r.from.code === hotelCurrency);
  if (!rate) {
    return amount;
  }
  return amount * rate.sellRate;
};

/**
 * Convert a price in cents from a hotel's native currency to the selected display currency.
 * Converts cents → dollars, applies the SELL exchange rate, then returns cents.
 *
 * @param amountCents - The price in cents in the hotel's native currency
 * @param hotelCurrency - The hotel's native currency code (e.g. 'USD', 'EUR')
 * @param selectedCurrencyData - The selected currency data from the available-currencies endpoint
 * @returns The converted price in cents, or the original amount if no matching rate is found
 */
export const convertPriceCents = (
  amountCents: number,
  hotelCurrency: TCurrencyCode,
  selectedCurrencyData: IAvailableCurrency
): number => {
  const rate = selectedCurrencyData.rates.find(r => r.from.code === hotelCurrency);
  if (!rate) {
    return amountCents;
  }
  return Math.round((amountCents / 100) * rate.sellRate * 100);
};
